'use strict'

const { EventEmitter } = require('events')
const Big = require('big.js').default
const debug = require('debug')('exec-txs')
const pSeries = require('p-series')
const pTap = require('p-tap')
const mean = require('lodash/mean')
const { chainList } = require('chain-list')

const { fromUnit } = require('metronome-utils')

const defaultHistoricalBlocks = 4
const eip1559Chains = chainList.filter(c => c.eip1559)

const calculateFee = function ({ transaction, receipt }, options) {
  if (options) {
    const { blockBaseFeePerGas } = options
    const { maxPriorityFeePerGas = 0 } = transaction
    const { baseFeePerGas } = blockBaseFeePerGas.find(
      ({ hash }) => hash.toLowerCase() === receipt.blockHash
    )
    return Big(baseFeePerGas)
      .plus(maxPriorityFeePerGas)
      .times(receipt.gasUsed)
      .toFixed()
  }
  return Big(transaction.gasPrice).times(receipt.gasUsed).toFixed()
}

const calculateTotalFee = (transactionsData, options) =>
  transactionsData
    .map(data => calculateFee(data, options))
    .reduce((total, fee) => Big(total).plus(fee), Big(0))
    .toFixed()

const createEstimateGasAndSend = (web3, emitter, overestimation = 1.25) =>
  function (method, transactionOptions, suffix) {
    const suffixed = event => `${event}${suffix ? `-${suffix}` : ''}`

    let hash
    let transactionPromise

    const estimateGas = function () {
      debug('Estimating gas')

      // estimateGas fails if EIP-1559 properties are sent
      // eslint-disable-next-line no-unused-vars
      const {
        autoClaim,
        maxFeePerGas,
        maxPriorityFeePerGas,
        type,
        useEip1559,
        ...estimationOptions
      } = transactionOptions

      const estimationPromise = method
        .estimateGas(estimationOptions)
        .then(
          pTap(function (gas) {
            debug('Gas needed is %d (x%s)', gas, overestimation.toFixed(2))
          })
        )
        .then(gas => Math.ceil(gas * overestimation))
        .then(function (gas) {
          // Emit the result
          emitter.emit(suffixed('estimatedGas'), gas)
          return gas
        })

      estimationPromise.catch(function (err) {
        debug('Gas estimation failed: %s', err.message)
        if (!emitter.listenerCount('error')) {
          return
        }
        emitter.emit('error', err)
      })

      return estimationPromise
    }

    const getTransaction = function () {
      if (!transactionPromise) {
        debug('Getting transaction %s', hash)
        transactionPromise = web3.eth.getTransaction(hash)
      }
      return transactionPromise
    }

    // Estimate the gas if not provided and add safety factor
    return Promise.resolve(transactionOptions.gas || estimateGas()).then(
      function (gas) {
        debug(
          'Sending transaction to %s',
          // When doing proxy calls, method._parent may be undefined.
          transactionOptions.to || method._parent?.options.address
        )

        // Remove our own custom properties to prevent the provider to complain
        // and send the transaction.
        const { autoClaim, dryRun, useEip1559, ...sendOptions } =
          transactionOptions

        const callOrSend = dryRun ? 'call' : 'send'

        const promiEvent = method[callOrSend](
          web3.currentProvider?.isMetaMask
            ? sendOptions
            : { ...sendOptions, gas }
        )

        // Listen for transaction events
        promiEvent.on('transactionHash', function (_hash) {
          hash = _hash
          debug('Transaction hash is %s', _hash)
          emitter.emit(suffixed('transactionHash'), _hash)
        })
        promiEvent.on('receipt', function (receipt) {
          debug('Transaction %s %s', receipt.status ? 'mined' : 'failed', hash)
          getTransaction()
            .then(function (transaction) {
              emitter.emit(suffixed('receipt'), { transaction, receipt })
            })
            .catch(function (err) {
              promiEvent.emit('error', err)
            })
        })
        promiEvent.on('error', function (err) {
          debug('Transaction failed %s: %s', hash || '?', err.message)
          if (!emitter.listenerCount('error')) {
            return
          }
          emitter.emit('error', err)
        })

        // Return the Web3 PromiEvent that will be casted to Promise
        return promiEvent.then(receipt =>
          getTransaction().then(transaction => ({ transaction, receipt }))
        )
      }
    )
  }

const createExecutor = ({ chainId, from, web3, overestimation }) =>
  function (transactionsPromise, parseResults, transactionOptions = {}) {
    const _from = transactionOptions.from || from
    let useEip1559 = eip1559Chains.find(c => c.chainId === chainId)
      ? transactionOptions.useEip1559
      : false

    const { historicalBlocks = defaultHistoricalBlocks } = transactionOptions

    const emitter = new EventEmitter()

    let errorEmitted = false
    emitter.on('error', function () {
      errorEmitted = true
    })

    const estimateGasAndSend = createEstimateGasAndSend(
      web3,
      emitter,
      overestimation
    )

    const calculatePriorityFeeHistory = feeHistory =>
      mean(feeHistory.reward.map(blockRewardFees => Number(blockRewardFees[0])))

    const addGasPrice = txs =>
      web3.eth.getGasPrice().then(gasPrice => ({ txs, gasPrice }))

    const addEip1559Fees = txs =>
      web3.eth
        .getFeeHistory(historicalBlocks, 'latest', [30])
        .then(function (feeHistory) {
          const baseFee = Big(
            web3.utils.hexToNumberString(
              feeHistory.baseFeePerGas[historicalBlocks]
            )
          )
          const maxPriorityFeePerGas =
            transactionOptions.maxPriorityFeePerGas ||
            calculatePriorityFeeHistory(feeHistory).toFixed()
          return {
            txs,
            baseFee: baseFee.toString(),
            maxPriorityFeePerGas,
            // same default as web3js.
            maxFeePerGas: baseFee.times(2).plus(Big(maxPriorityFeePerGas))
          }
        })

    const sendTransactions = function ({
      baseFee,
      txs,
      gasPrice,
      maxFeePerGas,
      maxPriorityFeePerGas
    }) {
      const expectedGas = txs.reduce((sum, { gas }) => sum + gas, 0)
      const expectedFee = useEip1559
        ? Big(baseFee).plus(maxPriorityFeePerGas).times(expectedGas).toFixed()
        : Big(gasPrice).times(expectedGas).toFixed()
      debug(
        'Expected fee in %d transaction(s) is %s ETH',
        txs.length,
        fromUnit(expectedFee)
      )

      emitter.emit('transactions', {
        expectedFee,
        suffixes: txs.map(({ suffix }) => suffix)
      })

      debug(
        'Sending %s transaction(s): %s',
        txs.length,
        txs.map(({ suffix }) => suffix).join(', ')
      )

      let eip1559options = {}
      if (useEip1559) {
        eip1559options = {
          maxPriorityFeePerGas: web3.currentProvider?.isMetaMask
            ? null
            : web3.utils.toHex(maxPriorityFeePerGas),
          maxFeePerGas: web3.currentProvider?.isMetaMask
            ? null
            : web3.utils.toHex(maxFeePerGas.toString()),
          type: '0x2'
        }
      }

      return web3.eth.getTransactionCount(_from, 'pending').then(count =>
        pSeries(
          txs.map(
            // If the value is not set, it is set by default to 0. Otherwise, it
            // will be set to "undefined" and the transaction may fail if the
            // provider is too picky.
            ({ method, suffix, value = '0' }, i) =>
              () =>
                estimateGasAndSend(
                  method,
                  {
                    from,
                    ...transactionOptions,
                    ...eip1559options,
                    value,
                    nonce: count + i
                  },
                  suffix
                )
          )
        )
      )
    }

    const getResult = transactionsData =>
      Promise.all([
        Promise.resolve(parseResults(transactionsData)),
        useEip1559
          ? Promise.all(
              transactionsData.map(txData =>
                web3.eth.getBlock(txData.receipt.blockHash)
              )
            )
          : Promise.resolve([])
      ]).then(function ([parsed, blocks]) {
        const result = {
          ...parsed,
          fees: calculateTotalFee(
            transactionsData,
            useEip1559
              ? {
                  blockBaseFeePerGas: blocks.map(block => ({
                    hash: block.hash,
                    baseFeePerGas: web3.utils.hexToNumberString(
                      block.baseFeePerGas
                    )
                  }))
                }
              : undefined
          ),
          raw: transactionsData,
          status: transactionsData[transactionsData.length - 1].receipt.status
        }

        debug('Total transaction fees paid %s ETH', fromUnit(result.fees))

        emitter.emit('result', result)
        return result
      })

    const promise = transactionsPromise
      .then(txs =>
        useEip1559
          ? addEip1559Fees(txs).catch(function () {
              useEip1559 = false
              return addGasPrice(txs)
            })
          : addGasPrice(txs)
      )
      .catch(function (err) {
        debug('Failed building transactions queue: %s', err.message)
        throw err
      })
      .then(sendTransactions)
      .then(getResult)

    promise.catch(function (err) {
      if (!emitter.listenerCount('error') || errorEmitted) {
        debug('Ignoring the error as it should be handled elsewhere')
        return
      }
      emitter.emit('error', err)
    })

    return {
      emitter,
      promise
    }
  }

module.exports = createExecutor
